import { z } from "zod";

// Zod schemas for payment API
export const PaymentSchema = z.object({
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
  id: z.number(),
  shift: z.any(),
  business: z.any(),
  worker: z.any(),
  assignment: z.any(),
  amount: z.number(),
  deduction: z.number(),
  payable: z.number(),
  shift_id: z.number(),
  business_id: z.number(),
  worker_id: z.string(),
  assignment_id: z.number(),
  payment_status: z.string(),
  collection_status: z.string().optional(),
  collect_payment_intent_id: z.string().nullable().optional(),
  send_payment_intent_id: z.string().nullable().optional(),
});

export const PaymentListResponseSchema = z.object({
  payments: z.array(PaymentSchema),
  summary: z.record(z.string(), z.any()),
});

export const PaymentSummaryItemSchema = z.object({
  amount: z.number(),
  deduction: z.number(),
  payable: z.number(),
  payment_id: z.number(),
  worker_id: z.string(),
  worker_name: z.string(),
  worker_avatar: z.string().nullable(),
  business_id: z.number(),
  business_name: z.string(),
  business_image: z.string().nullable(),
  shift_id: z.number(),
  shift_title: z.string().nullable(),
  assignment_id: z.number(),
  payment_status: z.string(),
  created_at: z.string(),
});

export const PaymentSummaryResponseSchema = z.object({
  message: z.string().nullable(),
  success: z.boolean(),
  payments: z.array(PaymentSummaryItemSchema),
  total: z.number(),
});

export type Payment = {
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  id: number;
  shift: any; // See full schema in API response
  business: any;
  worker: any;
  assignment: any;
  amount: number;
  deduction: number;
  payable: number;
  shift_id: number;
  business_id: number;
  worker_id: string;
  assignment_id: number;
  payment_status: string;
  collection_status?: string;
  collect_payment_intent_id?: string | null;
  send_payment_intent_id?: string | null;
};

export type PaymentListResponse = {
  payments: Array<Payment>;
  assignments?: Array<AssignmentItem>;
  summary: Record<string, any>;
};

export type AssignmentItem = {
  id: string;
  assignment_id: string;
  shift_name: string | null;
  business_name: string | null;
  wage: number;
  payable: number;
  assignment_status: string | null;
  status: string;
  start_date_time: string | null;
  created_at: string | null;
  type: "assignment";
};

export type PaymentSummaryItem = {
  amount: number;
  deduction: number;
  payable: number;
  payment_id: number;
  worker_id: string;
  worker_name: string;
  worker_avatar: string | null;
  business_id: number;
  business_name: string;
  business_image: string | null;
  shift_id: number;
  shift_title: string | null;
  assignment_id: number;
  payment_status: string;
  created_at: string;
};

export type PaymentSummaryResponse = {
  message: string | null;
  success: boolean;
  payments: Array<PaymentSummaryItem>;
  total: number;
};

export const SpendingsResponseSchema = z.object({
  message: z.string().nullable(),
  success: z.boolean(),
  total_spent: z.number().nullable(),
  jobs_completed: z.number().nullable(),
  pending_amount: z.number().nullable(),
  due_amount: z.number().nullable(),
  pending_jobs: z.number().nullable(),
  payments: z.array(PaymentSummaryItemSchema),
  page: z.number(),
  page_size: z.number(),
  total: z.number(),
});

export const EarningsResponseSchema = z.object({
  message: z.string().nullable(),
  success: z.boolean(),
  total_earned: z.number().nullable(),
  jobs_completed: z.number().nullable(),
  pending_amount: z.number().nullable(),
  due_amount: z.number().nullable(),
  pending_jobs: z.number().nullable(),
  payments: z.array(PaymentSummaryItemSchema),
  page: z.number(),
  page_size: z.number(),
  total: z.number(),
});

export type SpendingsResponse = {
  message: string | null;
  success: boolean;
  total_spent: number | null;
  jobs_completed: number | null;
  pending_amount: number | null;
  due_amount: number | null;
  pending_jobs: number | null;
  payments: Array<PaymentSummaryItem>;
  page: number;
  page_size: number;
  total: number;
};

export type EarningsResponse = {
  message: string | null;
  success: boolean;
  total_earned: number | null;
  jobs_completed: number | null;
  pending_amount: number | null;
  due_amount: number | null;
  pending_jobs: number | null;
  payments: Array<PaymentSummaryItem>;
  page: number;
  page_size: number;
  total: number;
};
